import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type AppRole = 'user' | 'agent' | 'agency_admin' | 'developer' | 'superadmin' | 'sales_agent' | 'sales_team_leader' | 'sales_manager' | 'marketing_agent' | 'marketing_team_leader' | 'marketing_manager' | 'it_staff' | 'accounting_staff' | 'hr_staff' | 'quality_control_lead' | 'quality_control_agent' | 'ad_operations_lead' | 'ad_operations_agent' | 'customer_support_agent' | 'customer_support_lead' | 'lead_ops_agent' | 'lead_ops_lead';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  roles: AppRole[];
  profile: any | null;
  loading: boolean;
  rolesLoading: boolean;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  roles: [],
  profile: null,
  loading: true,
  rolesLoading: true,
  signOut: async () => {},
  hasRole: () => false,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);

  const fetchUserData = async (userId: string) => {
    setRolesLoading(true);
    const [{ data: profileData }, { data: rolesData }] = await Promise.all([
      supabase.from('profiles').select('*').eq('user_id', userId).single(),
      supabase.from('user_roles').select('role').eq('user_id', userId),
    ]);

    // Block suspended accounts
    if (profileData && profileData.is_active === false) {
      toast.error('Your account has been suspended by your agency. Please contact them.');
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setRoles([]);
      setProfile(null);
      setRolesLoading(false);
      return;
    }

    setProfile(profileData);
    setRoles((rolesData || []).map((r: any) => r.role as AppRole));
    setRolesLoading(false);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setRolesLoading(true);
          setTimeout(() => fetchUserData(session.user.id), 0);
        } else {
          setProfile(null);
          setRoles([]);
          setRolesLoading(false);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserData(session.user.id);
      } else {
        setRolesLoading(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setRoles([]);
    setProfile(null);
  };

  const hasRole = (role: AppRole) => roles.includes(role);

  return (
    <AuthContext.Provider value={{ session, user, roles, profile, loading, rolesLoading, signOut, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}
